import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IconDefinition } from '@ant-design/icons-angular';
import { AppstoreAddOutline, PlusOutline } from '@ant-design/icons-angular/icons';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzTableCellDirective, NzTableComponent, NzTableModule, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { PackagingsService } from 'src/app/private/services/packagings.service';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { getDisplayUnit } from 'src/app/shared/utils/utils';
import { SourceEnum } from 'src/app/shared/enums/source.enum';
import { ModalFromToComponent } from '../modal-from-to/modal-from-to.component';
import { ModalPackagingsByFinalProductComponent } from '../modal-packagings-by-final-product/modal-packagings-by-final-product.component';
import { ModalRawMaterialsFinalProductOrPackagingComponent } from '../modal-raw-materials-final-product-or-packaging/modal-raw-materials-final-product-or-packaging.component';
import { FinalProductDto } from './../../../../shared/dto/final-product.dto';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';

const icons: IconDefinition[] = [
    PlusOutline,
    AppstoreAddOutline
];

@Component({
    selector: 'app-modal-final-products-by-order-process',
    standalone: true,
    imports: [
        NzModalFooterDirective,
        NzTagModule,
        NzTableModule,
        NzIconModule,
        FormsModule,
        ReactiveFormsModule,
        NzEmptyModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzModalTitleDirective,
        NzTableCellDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NzMessageModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        DecimalPipe
    ],
    templateUrl: './modal-final-products-by-order-process.component.html',
    styleUrl: './modal-final-products-by-order-process.component.scss'
})
export class ModalFinalProductsByOrderProcessComponent implements OnInit, AfterViewInit {
    @ViewChild('modalContent') private readonly _modalContent!: ElementRef;

    productionOrderProcess!: ProductionOrderProcessDto;
    finalProducts: FinalProductDto[] = [];
    isLoading: boolean = true;
    quantityToProduce: number = 0;
    packagingsByFinalProductId: Map<number, PackagingDto[]> = new Map();
    tableHeight!: number;
    data = inject(NZ_MODAL_DATA);

    /**
     * Devuelve la unidad de medida a mostrar según el proceso de producción
     */
    getDisplayUnit(): string {
        return getDisplayUnit(
            this.productionOrderProcess?.productionProcessId,
            this.productionOrderProcess?.productionProcess?.name,
            this.finalProducts?.[0]?.article?.measurementUnit?.abbreviation || ''
        );
    }

    private readonly toast = inject(NzMessageService);
    private readonly packagingsService = inject(PackagingsService);
    private readonly modalService = inject(NzModalService);
    private readonly modalRef = inject(NzModalRef);
    private readonly iconService = inject(NzIconService);

    async ngOnInit() {
        this.iconService.addIcon(...icons);
        this.productionOrderProcess = this.data?.productionOrderProcess;

        if (!this.productionOrderProcess) {
            this.toast.error('No se ha encontrado el proceso de orden de producción');
            this.modalRef.close();
            return;
        }

        if (!this.productionOrderProcess.productionProcessId) {
            this.toast.error('No se ha proporcionado el ID del proceso de producción');
            this.modalRef.close();
            return;
        }

        this.finalProducts = this.data?.finalProducts;

        if (this.finalProducts) {
            await this.loadPackagingsOfFinalProducts();
        } else {
            this.toast.error('No se han encontrado productos finales');
        }

        this.quantityToProduce = this.data?.quantityToProduce;
        this.isLoading = false;
    }

    async ngAfterViewInit(): Promise<void> {
        setTimeout(() => {
            this.tableHeight =
                (this._modalContent.nativeElement as HTMLImageElement)
                    .clientHeight - TITLE_TABLE_SIZE;
        });
    }

    getFinalProductPackagingsCount(finalProductId: number): number {
        return this.packagingsByFinalProductId.get(finalProductId)?.length || 0;
    }

    async loadPackagingsOfFinalProducts() {
        for (const finalProduct of this.finalProducts) {
            const packagings = await this.packagingsService.getPackagingsByFinalProductId(finalProduct.id!);
            console.log('Packagings recibidos del backend:', packagings);
            this.packagingsByFinalProductId.set(finalProduct.id!, packagings);
        }
    }

    showPackagings(finalProduct: FinalProductDto) {
        const modal = this.modalService.create({
            nzContent: ModalPackagingsByFinalProductComponent,
            nzStyle: { width: '85%', top: '20%' },
            nzBodyStyle: { height: '50vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Formatos de ${finalProduct.article?.name} - Cantidad total: ${finalProduct.quantityToProduce}`,
                finalProduct: finalProduct,
                packagings: this.packagingsByFinalProductId.get(finalProduct.id!),
                orderProcessId: this.productionOrderProcess?.id,
                productionProcessName: this.productionOrderProcess?.productionProcess?.name,
                quantityToProduce: this.quantityToProduce
            }
        });

        modal.componentInstance?.emitService.subscribe(async (packagings) => {
            this.packagingsByFinalProductId.set(finalProduct.id!, packagings);
            const finalProductIndex = this.finalProducts.findIndex(fp => fp.id === finalProduct.id);
            this.finalProducts[finalProductIndex].packagings = packagings;
        });
    }

    async onLinkMultipleRawMaterials() {
        try {
            if (this.finalProducts.length > 1) {
                const modalSelectWithInputs = await this.modalService.create({
                    nzContent: ModalFromToComponent,
                    nzStyle: { width: '50%', top: '20%' },
                    nzBodyStyle: { height: '30vh' },
                    nzClosable: false,
                    nzFooter: null,
                    nzData: {
                        nzTitle: `Indique de que producto final a que producto final desea añadir las materias primas`,
                        max: this.finalProducts.length
                    },
                });

                modalSelectWithInputs.componentInstance?.emitService.subscribe(async (response: { from: number, to: number }) => {
                    const from: number = response.from;
                    const to: number = response.to;
                    const finalProductsToAddRawMaterials: number[] = this.finalProducts.slice(from - 1, to).map(finalProduct => finalProduct.id!);

                    const modal = this.modalService.create({
                        nzContent: ModalRawMaterialsFinalProductOrPackagingComponent,
                        nzStyle: { width: '85%', top: '20%' },
                        nzBodyStyle: { height: '50vh' },
                        nzClosable: false,
                        nzFooter: null,
                        nzData: {
                            nzTitle: `Añadir materias primas a los productos finales del ${from} al ${to}`,
                            orderProcessId: this.productionOrderProcess?.id,
                            finalProductsToAddRawMaterialsIds: finalProductsToAddRawMaterials,
                            source: SourceEnum.FINAL_PRODUCT_MULTIPLE
                        }
                    });

                    modal.componentInstance?.emitService.subscribe(async (finalProducts: FinalProductDto[]) => {
                        finalProducts.forEach(updatedFinalProduct => {
                            const finalProductIndex = this.finalProducts.findIndex(fp => fp.id === updatedFinalProduct.id);
                            if (finalProductIndex !== -1) {
                                this.finalProducts[finalProductIndex] = updatedFinalProduct;
                            }
                        });
                    });
                });
            } else if (this.finalProducts.length === 1) {
                const modal = this.modalService.create({
                    nzContent: ModalRawMaterialsFinalProductOrPackagingComponent,
                    nzStyle: { width: '85%', top: '20%' },
                    nzBodyStyle: { height: '50vh' },
                    nzClosable: false,
                    nzFooter: null,
                    nzData: {
                        nzTitle: `Añadir materias primas al producto final`,
                        orderProcessId: this.productionOrderProcess?.id,
                        finalProductsToAddRawMaterialsIds: [this.finalProducts[0].id],
                        source: SourceEnum.FINAL_PRODUCT_MULTIPLE
                    }
                });

                modal.componentInstance?.emitService.subscribe(async (finalProducts: FinalProductDto[]) => {
                    finalProducts.forEach(updatedFinalProduct => {
                        const finalProductIndex = this.finalProducts.findIndex(fp => fp.id === updatedFinalProduct.id);
                        if (finalProductIndex !== -1) {
                            this.finalProducts[finalProductIndex] = updatedFinalProduct;
                        }
                    });
                });
            }
        } catch (error) {
            this.toast.error('Error al añadir las materias primas a los productos finales');
        }

    }

    onLinkRawMaterials(finalProduct: FinalProductDto) {
        const modal = this.modalService.create({
            nzContent: ModalRawMaterialsFinalProductOrPackagingComponent,
            nzStyle: { width: '85%', top: '20%' },
            nzBodyStyle: { height: '50vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Materias primas del producto final: ${finalProduct.article?.name || ''}`,
                finalProduct: finalProduct,
                source: SourceEnum.FINAL_PRODUCT,
                orderProcessId: this.productionOrderProcess?.id
            }
        });

        try {
            modal.componentInstance?.emitService.subscribe(async (updatedFinalProduct: FinalProductDto) => {
                const finalProductIndex = this.finalProducts.findIndex(fp => fp.id === finalProduct.id);
                if (finalProductIndex !== -1) {
                    this.finalProducts[finalProductIndex] = updatedFinalProduct;
                }
            });
        } catch (error) {
            this.toast.error('Error al obtener las materias primas del producto final');
        }

    }

    getFinalProductRawMaterialsCount(finalProductId: number): number {
        let rawMaterialsCount = 0;

        try {
            const finalProduct = this.finalProducts.find(fp => fp.id === finalProductId);
            if (!finalProduct) {
                this.toast.error('No se ha encontrado el producto final');
            } else {
                if (finalProduct.packagings && finalProduct.packagings.length > 0) {
                    rawMaterialsCount = finalProduct.packagings.reduce((acc, packaging) => acc + (packaging.rawMaterials?.length || 0), 0);
                } else {
                    rawMaterialsCount = finalProduct.rawMaterials?.length || 0;
                }
            }

        } catch (error) {
            this.toast.error('Error al obtener el número de materias primas del producto final');
        }

        return rawMaterialsCount;
    }

    handleClose() {
        this.modalRef.close();
    }
}
